import React, {useEffect, useState} from "react";
import {
    UniButton,
    UniCol,
    UniRow,
    UniForm,
    IntlMessageValue,
    IntlMessage, UniSpinner,
} from "../../../../@WUM/core/Elements";
import UniChecks from "../../../../@WUM/core/Elements/Form/Elements/Check";
import SunEditorConfig from "../../../ticketsystem/modules/components/SuneditorConfig";
import SunEditor from "suneditor-react";
import {useToast} from "../../../../@WUM/core/WebsocketNotification/ToastProvider/ToastProvider";
import {makeSubmitHandler} from "./save/SubmitHandler";
import {ResponseNotificationSettings} from "../../API/response/settingstimetracking";

interface SettingsState {

    activateAdminLateClockIn: boolean;
    adminLateClockInDeviation: string;
    activateAdminEarlyClockIn: boolean;
    adminEarlyClockInDeviation: string;
    activateAdminUnderwork: boolean;
    adminUnderworkHours: string;
    activateAdminWeeklyOvertime: boolean;
    adminWeeklyOvertimeHours: string;
    activateAdminWeeklyUndertime: boolean;
    adminWeeklyUndertimeHours: string;
    activateNoBreakInfo: boolean;
    noBreakMinTime: string;
    activateDayEndReminderPopup: boolean;
    endOfDayReminderPopupContent: string;
    dayEndReminderTime: string;
    activateDashboardInfoBanner: boolean;
    startDateDashboardInfoBanner: string;
    endDateDashboardInfoBanner: string;
    dashboardInfoBannerContent: string;
    dashboardInfoBannerHeadline: string;
}

const NotificationSettings = ({ onChangeUnsaved }: { onChangeUnsaved?: (value: boolean) => void }) => {

    const [unsaved, setUnsaved] = useState(false);

    const { showToast } = useToast();

    const markUnsaved = () => {
        if (!unsaved) {
            setUnsaved(true);
            onChangeUnsaved?.(true);
        }
    };

    const Response = ResponseNotificationSettings()

    useEffect(() => {
        if (!Response) return;
        const r = (Response as any).data ?? Response;

        setSettings({
            activateAdminLateClockIn: !!r.activateAdminLateClockIn,
            adminLateClockInDeviation: String(r.adminLateClockInDeviation ?? null),
            activateAdminEarlyClockIn: !!r.activateAdminEarlyClockIn,
            adminEarlyClockInDeviation: String(r.adminEarlyClockInDeviation ??  null),
            activateAdminUnderwork: !!r.activateAdminUnderwork,
            adminUnderworkHours: String(r.adminUnderworkHours ??  null),
            activateAdminWeeklyOvertime: !!r.activateAdminWeeklyOvertime,
            adminWeeklyOvertimeHours: String(r.adminWeeklyOvertimeHours ??  null),
            activateAdminWeeklyUndertime: !!r.activateAdminWeeklyUndertime,
            adminWeeklyUndertimeHours: String(r.adminWeeklyUndertimeHours ??  null),
            activateNoBreakInfo: !!r.activateNoBreakInfo,
            noBreakMinTime: String(r.noBreakMinTime ??  null),
            activateDayEndReminderPopup: !!r.activateDayEndReminderPopup,
            endOfDayReminderPopupContent: r.endOfDayReminderPopupContent ??  null,
            dayEndReminderTime: r.dayEndReminderTime ?? "17:00",
            activateDashboardInfoBanner: !!r.activateDashboardInfoBanner,
            startDateDashboardInfoBanner: r.startDateDashboardInfoBanner ?? null,
            endDateDashboardInfoBanner: r.endDateDashboardInfoBanner ?? null,
            dashboardInfoBannerContent: r.dashboardInfoBannerContent ??  null,
            dashboardInfoBannerHeadline: r.dashboardInfoBannerHeadline ??  null
        });
        setUnsaved(false);
    }, [Response]);


    const [validated, setValidated] = useState<boolean>(false);

    const [settings, setSettings] = useState<SettingsState | null>(null);

    if (Response === undefined || !settings) return <><UniSpinner /></>;


    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        if (
            name === "activateAdminLateClockIn" ||
            name === "activateAdminEarlyClockIn" ||
            name === "activateAdminUnderwork" ||
            name === "activateAdminWeeklyOvertime" ||
            name === "activateDayEndReminderPopup" ||
            name === "activateDashboardInfoBanner" ||
            name === "activateNoBreakInfo" ||
            name === "activateAdminWeeklyUndertime"
        ) {
            setSettings((prev) => (prev ? { ...prev, [name]: checked } : prev));
            markUnsaved();
        }
    };

    const setEditorContent = (fieldName: keyof SettingsState, html: string) => {
        setSettings(prev => (prev ? { ...prev, [fieldName]: html as any } : prev)); // <-- null-safe
        markUnsaved();
    };

    const handleSubmit = makeSubmitHandler({
        showToast,
        route: "work/notification/settings/save/",
        buildPayload: (form) => ({
            id: (Response as any)?.id,
            activateAdminLateClockIn: settings.activateAdminLateClockIn,
            adminLateClockInDeviation: (form as any).adminLateClockInDeviation.value,
            activateAdminEarlyClockIn: settings.activateAdminEarlyClockIn,
            adminEarlyClockInDeviation: (form as any).adminEarlyClockInDeviation.value,
            activateAdminUnderwork: settings.activateAdminUnderwork,
            adminUnderworkHours: (form as any).adminUnderworkHours.value,
            activateAdminWeeklyOvertime: settings.activateAdminWeeklyOvertime,
            activateAdminWeeklyUndertime: settings.activateAdminWeeklyUndertime,
            activateNoBreakInfo: settings.activateNoBreakInfo,
            noBreakMinTime: (form as any).noBreakMinTime.value,
            adminWeeklyOvertimeHours: (form as any).adminWeeklyOvertimeHours.value,
            adminWeeklyUndertimeHours: (form as any).adminWeeklyUndertimeHours.value,
            activateDayEndReminderPopup: settings.activateDayEndReminderPopup,
            endOfDayReminderPopupContent: settings.endOfDayReminderPopupContent,
            dayEndReminderTime: (form as any).dayEndReminderTime.value,
            startDateDashboardInfoBanner: (form as any).startDateDashboardInfoBanner.value,
            endDateDashboardInfoBanner: (form as any).endDateDashboardInfoBanner.value,
            activateDashboardInfoBanner: settings.activateDashboardInfoBanner,
            dashboardInfoBannerContent: settings.dashboardInfoBannerContent,
            dashboardInfoBannerHeadline: (form as any).dashboardInfoBannerHeadline.value
        }),
        successMessage: "Einstellungen gespeichert.",
        errorMessage: "Speichern fehlgeschlagen.",
        setUnsaved,
        onChangeUnsaved,
        setValidated,
        setEmptyStringsToNull: true
    });

    return (
        <>
            <UniForm validated={validated} Submit={handleSubmit} className={"p-4 worktime-settings"}>
                <p className={"border border-light-subtle rounded p-2 my-2 row text-justify"}>
                    {IntlMessage({locale: 'de', messageId: 'time.settings.general-explanation', Store: ''})}
                </p>
                <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                    {/* Zu spät eingeloggt */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateAdminLateClockIn"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.late", "") /* "Zu spät eingeloggt" */}
                            name="activateAdminLateClockIn"
                            checked={settings?.activateAdminLateClockIn}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.late.helper" /* "Warnung bei verspätetem Arbeitsbeginn" */ }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.late.deviation", "") /* "Abweichung" */} />
                            <UniForm.FormControl
                                type="number"
                                name="adminLateClockInDeviation"
                                defaultValue={settings.adminLateClockInDeviation}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.deviation.late.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>

                    {/* Zu früh eingeloggt */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateAdminEarlyClockIn"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.early", "") /* "Zu früh eingeloggt" */}
                            name="activateAdminEarlyClockIn"
                            checked={settings.activateAdminEarlyClockIn}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.early.helper" /* "Warnung bei zu frühem Arbeitsbeginn" */ }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.early.deviation", "") /* "Abweichung" */} />
                            <UniForm.FormControl
                                type="number"
                                name="adminEarlyClockInDeviation"
                                defaultValue={settings.adminEarlyClockInDeviation}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.early.deviation.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>

                    {/* Zu wenig gearbeitet */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateAdminUnderwork"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.underwork", "") /* "Zu wenig gearbeitet" */}
                            name="activateAdminUnderwork"
                            checked={settings.activateAdminUnderwork}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.underwork.helper" /* "Warnung, wenn Tagesarbeitszeit unterschritten" */ }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.underwork.hours", "") /* "Schwelle" */} />
                            <UniForm.FormControl
                                type="number"
                                name="adminUnderworkHours"
                                defaultValue={settings.adminUnderworkHours}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.underwork.hours.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>

                    {/* Wöchentliche Arbeitszeit überschritten */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateAdminWeeklyOvertime"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.weekly-overtime", "") /* "Wöchentliche Arbeitszeit überschritten" */}
                            name="activateAdminWeeklyOvertime"
                            checked={settings.activateAdminWeeklyOvertime}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.weekly-overtime.helper" /* "Warnung bei Überschreitung der Wochenarbeitszeit" */ }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.weekly-overtime.hours", "") /* "Schwelle" */} />
                            <UniForm.FormControl
                                type="number"
                                name="adminWeeklyOvertimeHours"
                                defaultValue={settings.adminWeeklyOvertimeHours}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.weekly-overtime.hours.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>

                    {/* Wöchentliche Arbeitszeit unterschritten */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateAdminWeeklyUndertime"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.weekly-undertime", "") /* "Wöchentliche Arbeitszeit überschritten" */}
                            name="activateAdminWeeklyUndertime"
                            checked={settings.activateAdminWeeklyUndertime}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.weekly-undertime.helper" /* "Warnung bei Überschreitung der Wochenarbeitszeit" */ }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.weekly-undertime.hours", "") /* "Schwelle" */} />
                            <UniForm.FormControl
                                type="number"
                                name="adminWeeklyUndertimeHours"
                                defaultValue={settings.adminWeeklyUndertimeHours}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.weekly-undertime.hours.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>

                    {/* Keine Pause übernommen */}
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateNoBreakInfo"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.admin-irregularity.no-break-info", "")}
                            name="activateNoBreakInfo"
                            checked={settings.activateNoBreakInfo}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.admin-irregularity.no-break-info.helper"}}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText class={""} text={IntlMessageValue("", "time.settings.admin-irregularity.no-break.hours", "") /* "Schwelle" */} />
                            <UniForm.FormControl
                                type="number"
                                name="noBreakMinTime"
                                defaultValue={settings.noBreakMinTime}
                                step={0.25}
                                required
                            />
                            <UniForm.InputGroupText class={""} text={"h"} />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.admin-irregularity.no-break.hours.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />

                        </UniForm.InputGroup>
                    </UniCol>
                </UniRow>


                {/* Tagesabschluss-Erinnerung */}
                <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                    <UniCol xs={12} md={6}>
                        <UniChecks
                            id="activateDayEndReminderPopup"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.activate-day-end-reminder-popup", "")}
                            name="activateDayEndReminderPopup"
                            checked={settings?.activateDayEndReminderPopup}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.activate-day-end-reminder-popup.helper" }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={6}>
                        <UniForm.InputGroup class={"mb-md-0 mb-3"}>
                            <UniForm.InputGroupText
                                class={""}
                                text={IntlMessageValue("", "time.settings.day-end-reminder-time", "")}
                            />
                            <UniForm.FormControl
                                type="time"
                                name="dayEndReminderTime"
                                defaultValue={settings?.dayEndReminderTime}
                                required
                            />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.day-end-reminder-time.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>
                    <UniCol xs={12} >
                        <SunEditor
                            key={"dashboard-info-banner"}
                            name={"endOfDayReminderPopupContent"}
                            setContents={settings.endOfDayReminderPopupContent}
                            setOptions={{
                                buttonList: SunEditorConfig(),
                                attributesWhitelist: {
                                    div: "id",
                                    p: "id",
                                    h: "id",
                                },
                            }}
                            height={"150px"}
                            onChange={(value) => setEditorContent("endOfDayReminderPopupContent", value)}
                        />
                    </UniCol>
                </UniRow>

                {/* Info-Banner Dashboard */}
                <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                    <UniCol xs={12} md={4}>
                        <UniChecks
                            id="activateDashboardInfoBanner"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.activate-dashboard-info-banner", "")}
                            name="activateDashboardInfoBanner"
                            checked={settings?.activateDashboardInfoBanner}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{ text: "time.settings.activate-dashboard-info-banner.helper" }}
                        />
                    </UniCol>
                    <UniCol xs={12} md={8}>
                        <UniRow>
                            <UniCol xs={12} md={6}>
                                <UniForm.InputGroup class={""}>
                                    <UniForm.InputGroupText
                                        class={""}
                                        text={IntlMessageValue("", "time.settings.day-end-reminder-time.start-date", "")}
                                    />
                                    <UniForm.FormControl
                                        type="date"
                                        name="startDateDashboardInfoBanner"
                                        defaultValue={settings?.startDateDashboardInfoBanner}
                                    />
                                    <UniForm.InputGroupHelpText
                                        text={"time.settings.day-end-reminder-time.start-date.helper"}
                                        class={"p-0 pe-1"}
                                        Store={""}
                                    />
                                </UniForm.InputGroup>
                            </UniCol>
                            <UniCol xs={12} md={6}>
                                <UniForm.InputGroup class={""}>
                                    <UniForm.InputGroupText
                                        class={""}
                                        text={IntlMessageValue("", "time.settings.day-end-reminder-time.end-date", "")}
                                    />
                                    <UniForm.FormControl
                                        type="date"
                                        name="endDateDashboardInfoBanner"
                                        defaultValue={settings?.endDateDashboardInfoBanner}
                                    />
                                    <UniForm.InputGroupHelpText
                                        text={"time.settings.day-end-reminder-time.end-date.helper"}
                                        class={"p-0 pe-1"}
                                        Store={""}
                                    />
                                </UniForm.InputGroup>
                            </UniCol>
                        </UniRow>


                    </UniCol>
                    <UniCol xs={12}>
                        <UniForm.InputGroup class={"mb-3"}>
                            <UniForm.InputGroupText
                                class={""}
                                text={IntlMessageValue("", "time.settings.activate-dashboard-info-banner.headline", "")}
                            />
                            <UniForm.FormControl
                                type="text"
                                name="dashboardInfoBannerHeadline"
                                defaultValue={settings?.dashboardInfoBannerHeadline}
                            />
                            <UniForm.InputGroupHelpText
                                text={"time.settings.activate-dashboard-info-banner.headline.helper"}
                                class={"p-0 pe-1"}
                                Store={""}
                            />
                        </UniForm.InputGroup>
                    </UniCol>
                    <UniCol md={12}>
                        <SunEditor
                            key={"dashboard-info-banner"}
                            name={"dashboardInfoBannerContent"}
                            setContents={settings.dashboardInfoBannerContent}
                            setOptions={{
                                buttonList: SunEditorConfig(),
                                attributesWhitelist: {
                                    div: "id",
                                    p: "id",
                                    h: "id",
                                },
                            }}
                            height={"150px"}
                            onChange={(value) => setEditorContent("dashboardInfoBannerContent", value)}
                        />
                    </UniCol>
                </UniRow>

                <UniButton type="submit" class={"float-end"}>
                    {IntlMessage({ locale: "de", messageId: "common.save", Store: "" })}
                </UniButton>
            </UniForm>
        </>
    );
};

export default NotificationSettings;
